import { z } from "zod";
import { TranslationValues } from "next-intl";

type GeneralInfoInputMessages = keyof IntlMessages["orderImportSchema1"];

const defaultMessages: Record<GeneralInfoInputMessages, string> = {
  "invalid-type": "This is not a valid entry for this field",
  "required": "This must be entered",
  "orderHeadQuotationId": "This is not a valid quotation Id",
  "orderHeadCustomerType": "Customer type must be either private or business",
  "titleIsRequired": "Title is required",
  "companyNameRequiredForBusiness": "Company Name must be set if business is selected",
  firstnameIsRequired: "Firstname is required",
  lastnameIsRequired: "Lastname is required",
  companyNameRequired: "Company name is required",
  addressIsRequired: "Address is required",
  zipcodeIsRequired: "Zipcode is required",
  cityIsRequired: "City is required",
  countryIsRequired: "Country is required",
  emailIsRequired: "Email is required",
  phonenumberIsRequired: "Phonenummer is required",
  invoiceAddressIsRequired: "Address is required",
  invoiceZipcodeIsRequired: "Zipcode is required",
  invoiceCityIsRequired: "City is required",
  invoiceCountryIsRequired: "Country is required",
  invoiceEmailIsRequired: "Email is required",
  invoicePhonenumberIsRequired: "Phonenumer is required"
};


// Define schemas using localized error messages
export const orderImportSchema1 = (
  t?: (key: GeneralInfoInputMessages, object?: TranslationValues | undefined ) => string
) => z.object({
  orderHeadQuotationId: z
    .string({
      invalid_type_error: t?.("orderHeadQuotationId") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .refine(value => {
      const num = Number(value)
      return value.length === 7 && num >= 1000000 && num <= 2000000;
      }, { 
        message: t?.('orderHeadQuotationId') ?? defaultMessages["orderHeadQuotationId"]
    }),
  orderHeadCustomerType: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .refine(value => value === 'private' || value === 'business', {
      message: t?.('orderHeadCustomerType') ?? defaultMessages['orderHeadCustomerType']
    }),
});


export const orderImportSchema2 = (
  t?: (key: GeneralInfoInputMessages, object?: TranslationValues | undefined ) => string
) => z.object({
  orderHeadCustomerType: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    }),
  orderHeadTitle: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, {message: t?.('titleIsRequired') ?? defaultMessages['titleIsRequired'] }),
  orderHeadFirstName: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('firstnameIsRequired') ?? defaultMessages["required"] }),
  orderHeadLastName: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('lastnameIsRequired') ?? defaultMessages["required"] }),
  orderHeadCompanyName: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    }),
  orderHeadVatId: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .optional(),
  orderHeadEmail: z
    .string({
      invalid_type_error: t?.("emailIsRequired") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .email({
      message: t?.('emailIsRequired') ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('emailIsRequired') ?? defaultMessages["required"] }),
}).refine(
  data => {
    if (data.orderHeadCustomerType === "business" && data.orderHeadCompanyName === "") {
      return false;
    }
    return true;
  }, {
    message: t?.('companyNameRequiredForBusiness') ?? defaultMessages['companyNameRequiredForBusiness'],
    path: ['orderHeadCompanyName']
  }
);

export const orderImportSchema3  = (
  t?: (key: GeneralInfoInputMessages, object?: TranslationValues | undefined ) => string
) => z.object({
  deliveryAddress1: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('addressIsRequired') ?? defaultMessages['required'] }),
  deliveryAddress2: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .optional(),
  deliveryAddress3: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .optional(),
  deliveryZipcode: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('zipcodeIsRequired') ?? defaultMessages["required"] }),
  deliveryCity: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('cityIsRequired') ?? defaultMessages["required"] }),
  deliveryState: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .optional(),
  deliveryCountry: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('countryIsRequired') ?? defaultMessages["required"] }),
  deliveryCompanyName: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .optional(),
  deliveryTitle: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('titleIsRequired') ?? defaultMessages["required"] }),
  deliveryFirstName: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('firstnameIsRequired') }),
  deliveryLastName: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('lastnameIsRequired') ?? defaultMessages["required"] }),
  deliveryEmail: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .email()
    .min(1, { message: t?.('emailIsRequired') ?? defaultMessages["required"] }),
  deliveryPhoneNumber: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("phonenumberIsRequired") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('phonenumberIsRequired') ?? defaultMessages["required"] }),
});


export const orderImportSchema4 = (
  t?: (key: GeneralInfoInputMessages, object?: TranslationValues | undefined ) => string
) => z.object({
  invoiceAddress1: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('invoiceAddressIsRequired') ?? defaultMessages['required'] }),
  invoiceAddress2: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .optional(),
  invoiceAddress3: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .optional(),
  invoiceZipcode: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('invoiceZipcodeIsRequired') ?? defaultMessages["required"] }),
  invoiceCity: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('invoiceCityIsRequired') ?? defaultMessages["required"]  }),
  invoiceState: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .optional(),
  invoiceCountry: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('invoiceCountryIsRequired') ?? defaultMessages["required"]  }),
  invoiceCompanyName: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .optional(),
  invoiceTitle: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('titleIsRequired') ?? defaultMessages["required"]  }),
  invoiceFirstName: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('firstnameIsRequired') ?? defaultMessages["required"]  }),
  invoiceLastName: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('lastnameIsRequired') ?? defaultMessages["required"]  }),
  invoiceEmail: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .email()
    .min(1, { message: t?.('invoiceEmailIsRequired') ?? defaultMessages["required"]  }),
  invoicePhoneNumber: z
    .string({
      invalid_type_error: t?.("invalid-type") ?? defaultMessages["invalid-type"],
      required_error: t?.("required") ?? defaultMessages["required"]
    })
    .min(1, { message: t?.('invoicePhonenumberIsRequired') ?? defaultMessages["required"]  }),
});

// Export types
export type TOrderImportSchema1 = z.infer<ReturnType<typeof orderImportSchema1>>;
export type TOrderImportSchema2 = z.infer<ReturnType<typeof orderImportSchema2>>;
export type TOrderImportSchema3 = z.infer<ReturnType<typeof orderImportSchema3>>;
export type TOrderImportSchema4 = z.infer<ReturnType<typeof orderImportSchema4>>;

// Export consent types
export * from './types/consent';
